import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useEffectiveWeekStartDay } from "../../../../hooks/useEffectiveWeekStartDay";
import { type OpenShift, type ShiftItem, ShiftType, type TransformedShift } from "../../../../utils/models";
import { groupShiftsByWeek } from "../groupShiftsByWeek";

export const useShiftWeekListMedicalState = (shifts: ShiftItem[], allOpenShifts: Record<string, OpenShift[]>) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const weekStartDay = useEffectiveWeekStartDay();
	const [expandedDays, setExpandedDays] = useState<string[]>([]);

	const groupedShifts = useMemo(() => groupShiftsByWeek(shifts, weekStartDay), [shifts, weekStartDay]);

	const getDaySummary = (dayShifts: (ShiftItem | TransformedShift)[] | undefined, dateKey: string) => {
		if (!dayShifts) {
			return { shiftCount: 0, requestCount: 0, sections: [] };
		}

		let shiftCount = 0;
		const sections = new Set<string>();

		// Count shifts and collect sections
		dayShifts.forEach((shift) => {
			// Count actual shifts (TransformedShift with startTime)
			if (shift.startTime && (shift.shiftType === ShiftType.Day || shift.shiftType === ShiftType.Night || shift.shiftType === ShiftType.All)) {
				shiftCount++;

				// Get section from location
				if ((shift as TransformedShift).location) {
					const location = chosenDepartment.locations.find((loc) => loc.location === (shift as TransformedShift).location);
					if (location?.section) {
						sections.add(location.section);
					}
				}
			}
		});

		// Count requests for this date
		const dateRequests = allOpenShifts[dateKey] || [];
		const requestCount = dateRequests.length;

		dateRequests.forEach((req) => {
			const location = chosenDepartment.locations.find((loc) => loc.location === req.location);
			if (location?.section) {
				sections.add(location.section);
			}
		});

		return {
			shiftCount,
			requestCount,
			sections: Array.from(sections).sort(),
		};
	};

	const groupShiftsBySection = (dayShifts: (ShiftItem | TransformedShift)[] | undefined) => {
		if (!dayShifts) {
			return {};
		}

		const shiftsBySection: Record<string, TransformedShift[]> = {};

		dayShifts.forEach((shift) => {
			// Only process TransformedShift items with startTime
			if (shift.startTime && (shift.shiftType === ShiftType.Day || shift.shiftType === ShiftType.Night || shift.shiftType === ShiftType.All)) {
				const transformedShift = shift as TransformedShift;
				let section = t("unassigned");

				if (transformedShift.location) {
					const location = chosenDepartment.locations.find((loc) => loc.location === transformedShift.location);
					if (location?.section) {
						section = location.section;
					}
				}

				if (!shiftsBySection[section]) {
					shiftsBySection[section] = [];
				}
				shiftsBySection[section].push(transformedShift);
			}
		});

		return shiftsBySection;
	};

	const toggleDay = (dateKey: string) => {
		setExpandedDays((prev) => (prev.includes(dateKey) ? prev.filter((key) => key !== dateKey) : [...prev, dateKey]));
	};

	return {
		groupedShifts,
		expandedDays,
		getDaySummary,
		groupShiftsBySection,
		toggleDay,
	};
};
